import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bookmark } from "lucide-react";
import { toast } from "sonner";
import { useApi, fileUrl } from "@/lib/api";
import { SOURCE_META, gradientFor } from "@/lib/sourceMeta";
import { timeAgo } from "@/lib/format";

export default function EntryCard({ entry, className = "" }) {
  const api = useApi();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const meta = SOURCE_META[entry.source_type] || SOURCE_META.website;
  const Icon = meta.icon;
  const img = fileUrl(entry.image_path);
  const isArchive = entry.type === "archive";

  const toggleSave = async (e) => {
    e.stopPropagation();
    try {
      await api.post(`/entries/${entry.id}/save`);
      qc.invalidateQueries({ queryKey: ["entries"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["entry", entry.id] });
      toast.success(entry.saved ? "Removed from saved" : "Saved to your collection");
    } catch {
      toast.error("Could not update saved state");
    }
  };

  return (
    <article
      data-testid={`entry-card-${entry.id}`}
      onClick={() => navigate(`/entry/${entry.id}`)}
      className={`break-inside-avoid mb-6 cursor-pointer group animate-fade-in ${className}`}
    >
      <div className="relative rounded-3xl overflow-hidden glass-card transition-[transform,box-shadow] duration-300 ease-out group-hover:-translate-y-1 group-hover:shadow-xl active:scale-[0.98]">
        {img ? (
          <img
            data-testid={`entry-image-${entry.id}`}
            src={img}
            alt={entry.title}
            loading="lazy"
            className="w-full object-cover"
          />
        ) : (
          <div className={`aspect-[4/3] bg-gradient-to-br ${gradientFor(entry.id)} flex items-center justify-center`}>
            <Icon className="h-10 w-10 text-white/80" strokeWidth={1.25} />
          </div>
        )}

        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full glass-card px-2.5 py-1">
          <Icon className="h-3 w-3" style={{ color: meta.color }} />
          <span className="text-[10px] font-semibold text-stone-600">{meta.label}</span>
        </span>

        <button
          data-testid={`save-button-${entry.id}`}
          onClick={toggleSave}
          aria-label={entry.saved ? "Unsave" : "Save"}
          className="absolute top-2.5 right-2.5 p-2 rounded-full glass-card active:scale-90 transition-transform"
        >
          <Bookmark
            className={`h-4 w-4 ${entry.saved ? "fill-primary text-primary" : "text-stone-500"}`}
          />
        </button>

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent px-4 pb-3.5 pt-10">
          {isArchive && (
            <span className="mb-1 inline-block rounded-full bg-white/20 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white backdrop-blur-md">
              Achievement
            </span>
          )}
          <h3 data-testid={`entry-title-${entry.id}`} className="font-semibold text-white text-[14px] leading-snug line-clamp-2 drop-shadow-sm">
            {entry.title}
          </h3>
          <p className="mt-0.5 text-[10px] text-white/60">{timeAgo(entry.created_at)}</p>
        </div>
      </div>
    </article>
  );
}
